import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateFormData } from '../../../redux/slices/listingSlice';
import FormInput from '../../ui/FormInput';

// Import constants from dedicated files
import { COMMON_CATEGORIES } from '../../../constants/categories';
import { COMMON_COLORS } from '../../../constants/colors';
import { COMMON_SIZES } from '../../../constants/sizes';
import { COMMON_MATERIALS } from '../../../constants/materials';
import { CONDITION_OPTIONS } from '../../../constants/conditions';

const DetailsSection = ({ formData, fieldErrors, handleInputChange, setFormData }) => {
  const dispatch = useDispatch();
  const currentFormData = useSelector((state) => state.listing.form.formData);
  const [showCustomSize, setShowCustomSize] = useState(currentFormData?.size === 'custom');

  const internalHandleInputChange = (e) => {
    console.log('[DetailsSection] internalHandleInputChange event:', e);
    if (e && e.target) {
      const { name, value, type, checked } = e.target;
      console.log('[DetailsSection] internalHandleInputChange - name:', name, 'value:', value);
      let val = type === 'checkbox' ? checked : value;
      if (type === 'number') {
        val = parseFloat(value);
        if (isNaN(val)) {
          val = '';
        }
      }
      if (handleInputChange) {
        handleInputChange(e);
      }
      if (currentFormData) {
        dispatch(updateFormData({ ...currentFormData, [name]: val }));
      } else {
        dispatch(updateFormData({ [name]: val }));
      }
      if (name === 'size') {
        setShowCustomSize(val === 'custom');
      }
    }
  };

  const data = formData || currentFormData;
  const onChangeHandler = handleInputChange || internalHandleInputChange;

  useEffect(() => {
    console.log('DetailsSection - formData:', data);
    console.log('DetailsSection - category field:', data?.category);
    console.log('DetailsSection - condition field:', data?.condition);
    console.log('DetailsSection - productType field:', data?.productType);
  }, [data]);

  const isClosetSharing = data?.productType === 'sharing';
  const isOriginalDesign = data?.productType === 'designer';
  const isVirtualFashion = data?.productType === 'virtual';
  const showMaterial = isClosetSharing || isOriginalDesign;
  const showCondition = isClosetSharing;
  const showBrand = isClosetSharing;

  // Prepare options for SUI Select with custom content for swatches
  const colorOptionsForSUI = [
    { key: 'placeholder', value: '', text: 'Select color', disabled: true }, // SUI uses `disabled` on option for placeholder
    ...COMMON_COLORS.map(color => ({
      key: color.value,
      value: color.value,
      text: color.label, // For search and accessibility
      content: (
        <span className="flex items-center">
          {color.bgClass && (
            <span 
              style={color.bgClass} 
              className="inline-block w-4 h-4 rounded-full mr-2 border border-gray-200"
            />
          )}
          {color.label}
        </span>
      )
    }))
  ];

  return (
    <div className="space-y-4 max-w-[1400px] mx-auto bg-white p-6 rounded-lg">
      <h2 className="text-heading font-semibold text-gray-800 mb-4">Details</h2>
      {isVirtualFashion && (
        <div className="p-4 bg-blue-50 rounded-lg mb-4">
          <p className="text-sm text-blue-700">
            For virtual fashion items, category is optional.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormInput
          as="select"
          label="Category"
          name="category"
          value={data?.category || ''}
          onChange={onChangeHandler}
          error={fieldErrors?.category}
          required={!isVirtualFashion}
          options={COMMON_CATEGORIES.map(cat => ({ key: cat.value, value: cat.value, text: cat.label }))}
          placeholder="Select category"
          fluid={false}
        />

        {!isVirtualFashion && (
          <>
            <div>
              <FormInput
                as="select"
                label="Size"
                name="size"
                value={data?.size || ''}
                onChange={onChangeHandler}
                error={fieldErrors?.size}
                required
                options={COMMON_SIZES.map(size => ({ key: size.value, value: size.value, text: size.label }))}
                placeholder="Select size"
                fluid={false}
              />
              {data?.size === 'custom' && (
                <div className="mt-2">
                  <FormInput
                    label="Custom Size Details"
                    name="customSizeDetails"
                    type="text"
                    value={data?.customSizeDetails || ''}
                    onChange={onChangeHandler}
                    placeholder="Enter measurements or size details"
                    required={data?.size === 'custom'}
                  />
                </div>
              )}
            </div>

            <FormInput
              as="select"
              label="Color"
              name="color"
              value={data?.color || ''}
              onChange={onChangeHandler}
              options={colorOptionsForSUI} // Use the new SUI-formatted options
              placeholder="Select color" // SUI Select uses this if value is empty
              error={fieldErrors?.color}
              required
              fluid={false}
              // Removed renderOption & renderSelected as SUI handles it via `content` in options
            />
          </>
        )}
      </div>

      {(showCondition || showMaterial || showBrand) && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {showCondition && (
            <FormInput
              as="select"
              label="Condition"
              name="condition"
              value={data?.condition || ''}
              onChange={onChangeHandler}
              error={fieldErrors?.condition}
              required={isClosetSharing}
              options={CONDITION_OPTIONS.map(cond => ({ key: cond.value, value: cond.value, text: cond.label }))}
              placeholder="Select condition"
              fluid={false}
            />
          )}
          {showMaterial && (
            <FormInput
              as="select"
              label="Material"
              name="material"
              value={data?.material || ''}
              onChange={onChangeHandler}
              error={fieldErrors?.material}
              options={COMMON_MATERIALS.map(material => ({ key: material.value, value: material.value, text: material.label }))}
              placeholder="Select material"
              fluid={false}
            />
          )}
          {showBrand && (
            <FormInput
              label="Brand"
              name="brand"
              type="text"
              fluid={false}
              value={data?.brand || ''}
              onChange={onChangeHandler}
              error={fieldErrors?.brand}
              placeholder="Enter brand name"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DetailsSection; 